/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: 通用图表卡片组件
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Card, Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import Loading from '@intelligence/components/Loading';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { ChartType } from '@intelligence/v3/enums/chart';

import type { IChartTypeOption } from './types';

import './index.scss';

/**
 * ChartCard 组件 Props
 */
interface IChartCardProps extends IBaseProps {
  /** 是否加载中 */
  loading?: boolean;
  /** 卡片标题 */
  title: string;
  /** 品类数据源（用于显示对应的 icon） */
  genreSource?: IGenreSource;
  /** 支持的图表类型列表 */
  chartTypes?: import('@intelligence/v3/enums/chart').ChartType[];
  /** 当前激活的图表类型 */
  chartType?: import('@intelligence/v3/enums/chart').ChartType;
  /** 图表类型切换回调 */
  onChartTypeChange?: ((type: import('@intelligence/v3/enums/chart').ChartType) => void) | null;
  /** 子元素（图表组件） */
  children: React.ReactNode;
}

/**
 * 默认图表类型列表
 */
const DEFAULT_TYPES = [ChartType.Bar, ChartType.Line, ChartType.Pie];

/**
 * 图表类型配置列表
 */
const CHART_TYPE_OPTIONS: IChartTypeOption[] = [
  {
    value: ChartType.Bar,
    icon: 'column-chart',
    context: 'dbi_bar_chart',
    disabled: false,
  },
  {
    value: ChartType.Line,
    icon: 'discount-chart',
    context: 'dbi_line_chart',
    disabled: false,
  },
  {
    value: ChartType.Pie,
    icon: 'pie-chart-icon',
    context: 'dbi_pie_chart',
    disabled: false,
  },
];

/**
 * ChartCard 通用图表卡片组件
 * @description 提供统一的图表卡片容器，支持标题展示和图表类型切换
 */
const ChartCard: React.FC<IChartCardProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    title,
    genreSource,
    chartTypes = DEFAULT_TYPES,
    chartType = ChartType.Bar,
    onChartTypeChange,
    children,
  } = props;

  const { formatMessage } = useIntlExtend();

  /**
   * 获取品类数据源 icon
   */
  const genreSourceIcon = useMemo(() => {
    if (!genreSource) {
      return null;
    }

    return getDataSourceIcon(genreSource);
  }, [genreSource]);

  /**
   * 过滤出支持的图表类型配置
   */
  const filteredChartTypeOptions = useMemo(() => {
    return CHART_TYPE_OPTIONS.filter((option) => {
      return !!chartTypes?.includes(option.value);
    });
  }, [chartTypes]);

  /**
   * 是否显示图表类型切换icon
   * 当支持的图表类型只有一种时不显示
   */
  const showChartTypeSwitch = filteredChartTypeOptions.length > 1;

  /**
   * 处理图表类型切换
   */
  const handleChartTypeChange = (type: ChartType): void => {
    if (type !== chartType) {
      onChartTypeChange?.(type);
    }
  };

  /**
   * 渲染图表类型切换操作区
   */
  const renderChartTypeSwitch = (): React.ReactNode => {
    if (!showChartTypeSwitch) {
      return null;
    }

    return (
      <div className="dbi-chart-card__type-switch">
        {filteredChartTypeOptions.map((option) => (
          <Bubble
            dark
            key={option.value}
            placement="top-end"
            placementOffset={[10, 15]}
            content={formatMessage({ id: option.context })}
          >
            <i
              className={classnames('dbi-chart-card__type-icon', {
                'dbi-chart-card__type-icon--active': option.value === chartType,
                'dbi-chart-card__type-icon--disabled': option.disabled,
              })}
              onClick={() => {
                if (!option.disabled) {
                  handleChartTypeChange(option.value);
                }
              }}
            >
              <SvgIcon className="dbi-chart-card__svg-icon" type={option.icon} />
            </i>
          </Bubble>
        ))}
      </div>
    );
  };

  return (
    <Card style={style} className={classnames('dbi-chart-card', className)}>
      <Card.Body
        operation={renderChartTypeSwitch()}
        title={
          <div className="dbi-chart-card__title">
            {genreSourceIcon && (
              <img className="dbi-chart-card__genre-source-icon" src={genreSourceIcon} alt={genreSource} />
            )}
            {title}
          </div>
        }
      >
        <div className="dbi-chart-card__content">{loading ? <Loading /> : children}</div>
      </Card.Body>
    </Card>
  );
};

export default ChartCard;
